import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import { LayoutDashboard } from 'lucide-react';
import api from '../services/api';
import { toast } from 'react-toastify';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Pie } from 'react-chartjs-2';
import '../styles/Builders.css';

ChartJS.register(ArcElement, Tooltip, Legend);

interface Project {
  _id: string;
  name: string;
}

interface ReportsData {
  totalCounts: {
    totalUnits: number;
    availableUnits: number;
    designStudioSubmitted: number;
    averageSpend: {
      withoutDecorDollar: string;
    };
  };
  designStudioRevenue: {
    totalRevenue: string;
    profit: string;
  };
  portalStats: {
    totalUnitsGrantedPortalAccess: number;
    totalUnitsRegistered: number;
  };
  dealStages: {
    unitReleased: number;
    reserved: number;
    firm: number;
  };
  popularColorCollections: Array<{
    name: string;
    count: number;
    percentage: string;
  }>;
  popularPackages: Array<{
    name: string;
    count: number;
    percentage: string;
  }>;
  popularOptions: Array<{
    name: string;
    count: number;
    percentage: string;
  }>;
  designStudioStatus: Array<{
    phase: string;
    notStarted: number;
    open: number;
    submitted: number;
    selectionFinal: number;
    waitingForPayment: number;
    completed: number;
    cancelledByCustomer: number;
    cancelledByBuilder: number;
  }>;
  parkingSelection: {
    open: number;
    notStarted: number;
    submitted: number;
    selectionFinal: number;
  };
  lockerSelection: {
    open: number;
    notStarted: number;
  };
  bicycleSelection: {
    open: number;
    notStarted: number;
    completed: number;
  };
}

const ReportsDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { workspaceId } = useParams<{ workspaceId: string }>();

  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [reportsData, setReportsData] = useState<ReportsData | null>(null);
  const [loadingReports, setLoadingReports] = useState(false);
  const [selectedPhase, setSelectedPhase] = useState<string | null>(null);

  const handleBackToWorkspaces = () => {
    navigate('/workspaces');
  };

  const fetchProjects = useCallback(async () => {
    if (!workspaceId) return;

    try {
      setLoading(true);
      const response = await api.get(`/workspaces/${workspaceId}/projects`);
      setProjects(response.data.projects || []);

      // Auto-select first project if available
      if (response.data.projects && response.data.projects.length > 0) {
        setSelectedProjectId(response.data.projects[0]._id);
      }
    } catch (err: any) {
      console.error('Error fetching projects:', err);
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  const fetchReportsData = useCallback(async () => {
    if (!selectedProjectId || !workspaceId) return;

    try {
      setLoadingReports(true);
      const response = await api.get(`/workspaces/${workspaceId}/reports`, {
        params: { projectId: selectedProjectId }
      });
      setReportsData(response.data);
    } catch (err: any) {
      console.error('Error fetching reports data:', err);
      toast.error('Failed to load reports data');
    } finally {
      setLoadingReports(false);
    }
  }, [workspaceId, selectedProjectId]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  useEffect(() => {
    if (selectedProjectId) {
      fetchReportsData();
    }
  }, [selectedProjectId, fetchReportsData]);

  const handleProjectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedProjectId(e.target.value);
  };

  if (loading) {
    return (
      <AppLayout onBack={handleBackToWorkspaces}>
        <div className="builders-container">
          <div className="builders-loading">
            <div className="builders-loading-spinner" />
          </div>
        </div>
      </AppLayout>
    );
  }

  if (projects.length === 0) {
    return (
      <AppLayout onBack={handleBackToWorkspaces}>
        <div className="builders-container">
          <div className="builders-section-header" style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start'
          }}>
            <div>
              <h1 className="builders-section-title">Reports Dashboard</h1>
              <p className="builders-section-subtitle">
                Overview of all reports
              </p>
            </div>
          </div>
          <div className="builders-empty-state">
            <LayoutDashboard size={48} color="#A89088" style={{ marginBottom: '16px' }} />
            <p>No projects found. Please create a project first.</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout onBack={handleBackToWorkspaces}>
      <div className="builders-container">
        {/* Page Header with Project Dropdown */}
        <div className="builders-section-header" style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start'
        }}>
          <div>
            <h1 className="builders-section-title">Reports Dashboard</h1>
            <p className="builders-section-subtitle">
              Overview of all reports
            </p>
          </div>
          <div style={{ marginTop: '4px' }}>
            <select
              className="form-input"
              value={selectedProjectId}
              onChange={handleProjectChange}
              disabled={loading || projects.length === 0}
              style={{ minWidth: '280px' }}
            >
              <option value="">Select Project</option>
              {projects.map(project => (
                <option key={project._id} value={project._id}>
                  {project.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {loadingReports ? (
          <div className="builders-loading">
            <div className="builders-loading-spinner" />
          </div>
        ) : reportsData ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {/* Stats Parent Card */}
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 2px 8px rgba(72, 24, 47, 0.1)',
              border: '1px solid #F3F4F6'
            }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                gap: '20px'
              }}>
                {/* Total Units */}
              <div style={{
                background: 'white',
                borderRadius: '12px',
                padding: '24px',
                boxShadow: '0 2px 8px rgba(72, 24, 47, 0.1)',
                border: '1px solid #F3F4F6'
              }}>
                <div style={{
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#6B7280',
                  marginBottom: '12px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  TOTAL UNITS
                </div>
                <div style={{
                  fontSize: '48px',
                  fontWeight: 700,
                  color: '#A89088'
                }}>
                  {reportsData.totalCounts.totalUnits}
                </div>
              </div>

              {/* Available Units */}
              <div style={{
                background: 'white',
                borderRadius: '12px',
                padding: '24px',
                boxShadow: '0 2px 8px rgba(72, 24, 47, 0.1)',
                border: '1px solid #F3F4F6'
              }}>
                <div style={{
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#6B7280',
                  marginBottom: '12px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  AVAILABLE UNITS
                </div>
                <div style={{
                  fontSize: '48px',
                  fontWeight: 700,
                  color: '#A89088'
                }}>
                  {reportsData.totalCounts.availableUnits}
                </div>
              </div>

              {/* Design Studio Submitted */}
              <div style={{
                background: 'white',
                borderRadius: '12px',
                padding: '24px',
                boxShadow: '0 2px 8px rgba(72, 24, 47, 0.1)',
                border: '1px solid #F3F4F6'
              }}>
                <div style={{
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#6B7280',
                  marginBottom: '12px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  DESIGN STUDIO SUBMITTED
                </div>
                <div style={{
                  fontSize: '16px',
                  fontWeight: 600,
                  color: '#A89088',
                  marginBottom: '8px'
                }}>
                  Phase one : {reportsData.totalCounts.designStudioSubmitted}
                </div>
              </div>

              {/* Average Spend */}
              <div style={{
                background: 'white',
                borderRadius: '12px',
                padding: '24px',
                boxShadow: '0 2px 8px rgba(72, 24, 47, 0.1)',
                border: '1px solid #F3F4F6'
              }}>
                <div style={{
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#6B7280',
                  marginBottom: '12px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  AVERAGE SPEND
                </div>
                <div style={{
                  fontSize: '16px',
                  fontWeight: 600,
                  color: '#A89088',
                  marginBottom: '4px'
                }}>
                  Phase one :
                </div>
                <div style={{
                  fontSize: '14px',
                  color: '#6B7280'
                }}>
                  Without Decor Dollar: ${reportsData.totalCounts.averageSpend.withoutDecorDollar}
                </div>
              </div>

              {/* Design Studio Revenue */}
              <div style={{
                background: 'white',
                borderRadius: '12px',
                padding: '24px',
                boxShadow: '0 2px 8px rgba(72, 24, 47, 0.1)',
                border: '1px solid #F3F4F6'
              }}>
                <div style={{
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#6B7280',
                  marginBottom: '12px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  DESIGN STUDIO REVENUE
                </div>
                <div style={{
                  fontSize: '16px',
                  fontWeight: 600,
                  color: '#A89088',
                  marginBottom: '4px'
                }}>
                  Phase one :
                </div>
                <div style={{
                  fontSize: '14px',
                  color: '#6B7280',
                  marginBottom: '4px'
                }}>
                  Total Revenue : ${reportsData.designStudioRevenue.totalRevenue}
                </div>
                <div style={{
                  fontSize: '14px',
                  color: '#6B7280'
                }}>
                  Profit : ${reportsData.designStudioRevenue.profit}
                </div>
              </div>

              {/* Total Units - Granted Access to Portal */}
              <div style={{
                background: 'white',
                borderRadius: '12px',
                padding: '24px',
                boxShadow: '0 2px 8px rgba(72, 24, 47, 0.1)',
                border: '1px solid #F3F4F6'
              }}>
                <div style={{
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#6B7280',
                  marginBottom: '12px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  TOTAL UNITS - GRANTED ACCESS TO PORTAL
                </div>
                <div style={{
                  fontSize: '48px',
                  fontWeight: 700,
                  color: '#A89088'
                }}>
                  {reportsData.portalStats.totalUnitsGrantedPortalAccess}
                </div>
              </div>

              {/* Total Units Registered */}
              <div style={{
                background: 'white',
                borderRadius: '12px',
                padding: '24px',
                boxShadow: '0 2px 8px rgba(72, 24, 47, 0.1)',
                border: '1px solid #F3F4F6'
              }}>
                <div style={{
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#6B7280',
                  marginBottom: '12px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  TOTAL UNITS REGISTERED
                </div>
                <div style={{
                  fontSize: '48px',
                  fontWeight: 700,
                  color: '#A89088'
                }}>
                  {reportsData.portalStats.totalUnitsRegistered}
                </div>
              </div>

              {/* Deal Stage */}
              <div style={{
                background: 'white',
                borderRadius: '12px',
                padding: '24px',
                boxShadow: '0 2px 8px rgba(72, 24, 47, 0.1)',
                border: '1px solid #F3F4F6'
              }}>
                <div style={{
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#6B7280',
                  marginBottom: '12px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  DEAL STAGE
                </div>
                <div style={{
                  fontSize: '14px',
                  color: '#6B7280',
                  marginBottom: '4px'
                }}>
                  UNIT RELEASED - {reportsData.dealStages.unitReleased}
                </div>
                <div style={{
                  fontSize: '14px',
                  color: '#6B7280',
                  marginBottom: '4px'
                }}>
                  RESERVED - {reportsData.dealStages.reserved}
                </div>
                <div style={{
                  fontSize: '14px',
                  color: '#6B7280'
                }}>
                  FIRM - {reportsData.dealStages.firm}
                </div>
              </div>

              {/* Popular Color Collections */}
              <div style={{
                background: 'white',
                borderRadius: '12px',
                padding: '24px',
                boxShadow: '0 2px 8px rgba(72, 24, 47, 0.1)',
                border: '1px solid #F3F4F6'
              }}>
                <div style={{
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#6B7280',
                  marginBottom: '12px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  POPULAR COLOR COLLECTIONS
                </div>
                {reportsData.popularColorCollections.length > 0 ? (
                  reportsData.popularColorCollections.map((item, index) => (
                    <div key={index} style={{
                      fontSize: '14px',
                      color: '#A89088',
                      marginBottom: '4px'
                    }}>
                      {item.name} - {item.percentage}%
                    </div>
                  ))
                ) : (
                  <div style={{ fontSize: '14px', color: '#9CA3AF' }}>No data available</div>
                )}
              </div>

              {/* Popular Packages */}
              <div style={{
                background: 'white',
                borderRadius: '12px',
                padding: '24px',
                boxShadow: '0 2px 8px rgba(72, 24, 47, 0.1)',
                border: '1px solid #F3F4F6'
              }}>
                <div style={{
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#6B7280',
                  marginBottom: '12px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  POPULAR PACKAGES
                </div>
                {reportsData.popularPackages.length > 0 ? (
                  reportsData.popularPackages.map((item, index) => (
                    <div key={index} style={{
                      fontSize: '14px',
                      color: '#A89088',
                      marginBottom: '4px'
                    }}>
                      {item.name} - {item.percentage}%
                    </div>
                  ))
                ) : (
                  <div style={{ fontSize: '14px', color: '#9CA3AF' }}>No data available</div>
                )}
              </div>

              {/* Popular Options */}
              <div style={{
                background: 'white',
                borderRadius: '12px',
                padding: '24px',
                boxShadow: '0 2px 8px rgba(72, 24, 47, 0.1)',
                border: '1px solid #F3F4F6'
              }}>
                <div style={{
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#6B7280',
                  marginBottom: '12px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  POPULAR OPTIONS
                </div>
                {reportsData.popularOptions.length > 0 ? (
                  reportsData.popularOptions.map((item, index) => (
                    <div key={index} style={{
                      fontSize: '14px',
                      color: '#A89088',
                      marginBottom: '4px'
                    }}>
                      {item.name} - {item.percentage}%
                    </div>
                  ))
                ) : (
                  <div style={{ fontSize: '14px', color: '#9CA3AF' }}>No data available</div>
                )}
              </div>
            </div>
          </div>

          {/* Graphs Parent Card */}
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 2px 8px rgba(72, 24, 47, 0.1)',
            border: '1px solid #F3F4F6'
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: '20px'
            }}>
              {/* Design Studio Phase - Overview */}
              <div style={{
                background: 'white',
                borderRadius: '12px',
                padding: '24px',
                boxShadow: '0 2px 8px rgba(72, 24, 47, 0.1)',
                border: '1px solid #F3F4F6'
              }}>
                <div style={{
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#6B7280',
                  marginBottom: '16px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  DESIGN STUDIO PHASE
                </div>
                <div style={{ width: '100%', maxWidth: '300px', margin: '0 auto' }}>
                  <Pie
                    data={{
                      labels: reportsData.designStudioStatus.map(phase => phase.phase.toUpperCase()),
                      datasets: [{
                        data: reportsData.designStudioStatus.map(phase =>
                          phase.notStarted + phase.open + phase.submitted +
                          phase.selectionFinal + phase.waitingForPayment +
                          phase.completed + phase.cancelledByCustomer +
                          phase.cancelledByBuilder
                        ),
                        backgroundColor: ['#00BCD4', '#009688', '#4CAF50', '#8BC34A', '#CDDC39'],
                        borderWidth: 0
                      }]
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: true,
                      onClick: (_event, elements) => {
                        if (elements.length > 0) {
                          const index = elements[0].index;
                          setSelectedPhase(reportsData.designStudioStatus[index].phase);
                        }
                      },
                      plugins: {
                        legend: {
                          position: 'left',
                          labels: {
                            boxWidth: 15,
                            padding: 10,
                            font: { size: 11 }
                          }
                        },
                        tooltip: {
                          callbacks: {
                            label: (context) => {
                              const label = context.label || '';
                              const value = context.parsed || 0;
                              return `${label}: ${value}`;
                            }
                          }
                        }
                      }
                    }}
                  />
                </div>
              </div>

              {/* Design Studio Phase Detail - Show when a phase is selected */}
              {selectedPhase !== null && (() => {
                const phaseData = reportsData.designStudioStatus.find(p => p.phase === selectedPhase);
                if (!phaseData) return null;

                const phaseChartData = {
                  labels: [] as string[],
                  datasets: [{
                    data: [] as number[],
                    backgroundColor: [] as string[],
                    borderWidth: 0
                  }]
                };

                if (phaseData.notStarted > 0) {
                  phaseChartData.labels.push('NOT STARTED');
                  phaseChartData.datasets[0].data.push(phaseData.notStarted);
                  phaseChartData.datasets[0].backgroundColor.push('#9CA3AF');
                }
                if (phaseData.open > 0) {
                  phaseChartData.labels.push('OPEN');
                  phaseChartData.datasets[0].data.push(phaseData.open);
                  phaseChartData.datasets[0].backgroundColor.push('#6B7280');
                }
                if (phaseData.submitted > 0) {
                  phaseChartData.labels.push('SUBMITTED');
                  phaseChartData.datasets[0].data.push(phaseData.submitted);
                  phaseChartData.datasets[0].backgroundColor.push('#10B981');
                }
                if (phaseData.selectionFinal > 0) {
                  phaseChartData.labels.push('SELECTION FINAL');
                  phaseChartData.datasets[0].data.push(phaseData.selectionFinal);
                  phaseChartData.datasets[0].backgroundColor.push('#3B82F6');
                }
                if (phaseData.waitingForPayment > 0) {
                  phaseChartData.labels.push('WAITING FOR PAYMENT');
                  phaseChartData.datasets[0].data.push(phaseData.waitingForPayment);
                  phaseChartData.datasets[0].backgroundColor.push('#F59E0B');
                }
                if (phaseData.completed > 0) {
                  phaseChartData.labels.push('COMPLETED');
                  phaseChartData.datasets[0].data.push(phaseData.completed);
                  phaseChartData.datasets[0].backgroundColor.push('#6B8E23');
                }
                if (phaseData.cancelledByCustomer > 0) {
                  phaseChartData.labels.push('CANCELLED - BY CUSTOMER');
                  phaseChartData.datasets[0].data.push(phaseData.cancelledByCustomer);
                  phaseChartData.datasets[0].backgroundColor.push('#EF4444');
                }
                if (phaseData.cancelledByBuilder > 0) {
                  phaseChartData.labels.push('CANCELLED - BY BUILDER');
                  phaseChartData.datasets[0].data.push(phaseData.cancelledByBuilder);
                  phaseChartData.datasets[0].backgroundColor.push('#DC2626');
                }

                return (
                  <div style={{
                    background: 'white',
                    borderRadius: '12px',
                    padding: '24px',
                    boxShadow: '0 2px 8px rgba(72, 24, 47, 0.1)',
                    border: '1px solid #F3F4F6'
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '16px'
                    }}>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: 500,
                        color: '#6B7280',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        STATUS OF {selectedPhase.toUpperCase()}
                      </div>
                      <button
                        onClick={() => setSelectedPhase(null)}
                        style={{
                          background: '#A89088',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '6px 12px',
                          fontSize: '12px',
                          cursor: 'pointer',
                          fontWeight: 500
                        }}
                      >
                        Close
                      </button>
                    </div>
                    <div style={{ width: '100%', maxWidth: '300px', margin: '0 auto' }}>
                      <Pie
                        data={phaseChartData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          plugins: {
                            legend: {
                              position: 'left',
                              labels: {
                                boxWidth: 15,
                                padding: 10,
                                font: { size: 11 }
                              }
                            },
                            tooltip: {
                              callbacks: {
                                label: (context) => {
                                  const label = context.label || '';
                                  const value = context.parsed || 0;
                                  return `${label}: ${value}`;
                                }
                              }
                            }
                          }
                        }}
                      />
                    </div>
                  </div>
                );
              })()}

              {/* Parking Selection Status */}
              <div style={{
                background: 'white',
                borderRadius: '12px',
                padding: '24px',
                boxShadow: '0 2px 8px rgba(72, 24, 47, 0.1)',
                border: '1px solid #F3F4F6'
              }}>
                <div style={{
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#6B7280',
                  marginBottom: '16px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  PARKING SELECTION STATUS
                </div>
                <div style={{ width: '100%', maxWidth: '300px', margin: '0 auto' }}>
                  <Pie
                    data={{
                      labels: [
                        ...(reportsData.parkingSelection.open > 0 ? ['OPEN'] : []),
                        ...(reportsData.parkingSelection.submitted > 0 ? ['SUBMITTED'] : []),
                        ...(reportsData.parkingSelection.selectionFinal > 0 ? ['SELECTION FINAL'] : []),
                        ...(reportsData.parkingSelection.notStarted > 0 ? ['NOT STARTED'] : [])
                      ],
                      datasets: [{
                        data: [
                          ...(reportsData.parkingSelection.open > 0 ? [reportsData.parkingSelection.open] : []),
                          ...(reportsData.parkingSelection.submitted > 0 ? [reportsData.parkingSelection.submitted] : []),
                          ...(reportsData.parkingSelection.selectionFinal > 0 ? [reportsData.parkingSelection.selectionFinal] : []),
                          ...(reportsData.parkingSelection.notStarted > 0 ? [reportsData.parkingSelection.notStarted] : [])
                        ],
                        backgroundColor: [
                          ...(reportsData.parkingSelection.open > 0 ? ['#D1C4B0'] : []),
                          ...(reportsData.parkingSelection.submitted > 0 ? ['#10B981'] : []),
                          ...(reportsData.parkingSelection.selectionFinal > 0 ? ['#3B82F6'] : []),
                          ...(reportsData.parkingSelection.notStarted > 0 ? ['#9CA3AF'] : [])
                        ],
                        borderWidth: 0
                      }]
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: true,
                      plugins: {
                        legend: {
                          position: 'left',
                          labels: {
                            boxWidth: 15,
                            padding: 10,
                            font: { size: 11 }
                          }
                        },
                        tooltip: {
                          callbacks: {
                            label: (context) => {
                              const label = context.label || '';
                              const value = context.parsed || 0;
                              return `${label}: ${value}`;
                            }
                          }
                        }
                      }
                    }}
                  />
                </div>
              </div>

              {/* Locker Selection Status */}
              <div style={{
                background: 'white',
                borderRadius: '12px',
                padding: '24px',
                boxShadow: '0 2px 8px rgba(72, 24, 47, 0.1)',
                border: '1px solid #F3F4F6'
              }}>
                <div style={{
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#6B7280',
                  marginBottom: '16px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  LOCKER SELECTION STATUS
                </div>
                <div style={{ width: '100%', maxWidth: '300px', margin: '0 auto' }}>
                  <Pie
                    data={{
                      labels: [
                        ...(reportsData.lockerSelection.open > 0 ? ['OPEN'] : []),
                        ...(reportsData.lockerSelection.notStarted > 0 ? ['NOT STARTED'] : [])
                      ],
                      datasets: [{
                        data: [
                          ...(reportsData.lockerSelection.open > 0 ? [reportsData.lockerSelection.open] : []),
                          ...(reportsData.lockerSelection.notStarted > 0 ? [reportsData.lockerSelection.notStarted] : [])
                        ],
                        backgroundColor: [
                          ...(reportsData.lockerSelection.open > 0 ? ['#EF4444'] : []),
                          ...(reportsData.lockerSelection.notStarted > 0 ? ['#A89088'] : [])
                        ],
                        borderWidth: 0
                      }]
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: true,
                      plugins: {
                        legend: {
                          position: 'left',
                          labels: {
                            boxWidth: 15,
                            padding: 10,
                            font: { size: 11 }
                          }
                        },
                        tooltip: {
                          callbacks: {
                            label: (context) => {
                              const label = context.label || '';
                              const value = context.parsed || 0;
                              return `${label}: ${value}`;
                            }
                          }
                        }
                      }
                    }}
                  />
                </div>
              </div>

              {/* Bicycle Selection Status */}
              <div style={{
                background: 'white',
                borderRadius: '12px',
                padding: '24px',
                boxShadow: '0 2px 8px rgba(72, 24, 47, 0.1)',
                border: '1px solid #F3F4F6'
              }}>
                <div style={{
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#6B7280',
                  marginBottom: '16px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  BICYCLE SELECTION STATUS
                </div>
                <div style={{ width: '100%', maxWidth: '300px', margin: '0 auto' }}>
                  <Pie
                    data={{
                      labels: [
                        ...(reportsData.bicycleSelection.open > 0 ? ['OPEN'] : []),
                        ...(reportsData.bicycleSelection.notStarted > 0 ? ['NOT STARTED'] : []),
                        ...(reportsData.bicycleSelection.completed > 0 ? ['COMPLETED'] : [])
                      ],
                      datasets: [{
                        data: [
                          ...(reportsData.bicycleSelection.open > 0 ? [reportsData.bicycleSelection.open] : []),
                          ...(reportsData.bicycleSelection.notStarted > 0 ? [reportsData.bicycleSelection.notStarted] : []),
                          ...(reportsData.bicycleSelection.completed > 0 ? [reportsData.bicycleSelection.completed] : [])
                        ],
                        backgroundColor: [
                          ...(reportsData.bicycleSelection.open > 0 ? ['#7C2D12'] : []),
                          ...(reportsData.bicycleSelection.notStarted > 0 ? ['#3B82F6'] : []),
                          ...(reportsData.bicycleSelection.completed > 0 ? ['#10B981'] : [])
                        ],
                        borderWidth: 0
                      }]
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: true,
                      plugins: {
                        legend: {
                          position: 'left',
                          labels: {
                            boxWidth: 15,
                            padding: 10,
                            font: { size: 11 }
                          }
                        },
                        tooltip: {
                          callbacks: {
                            label: (context) => {
                              const label = context.label || '';
                              const value = context.parsed || 0;
                              return `${label}: ${value}`;
                            }
                          }
                        }
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        ) : (
          <div className="builders-empty-state">
            <LayoutDashboard size={48} color="#A89088" style={{ marginBottom: '16px' }} />
            <p>Select a project to view reports</p>
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default ReportsDashboard;
